<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    ref="datepickerRoot"
    :class="classes"
    :style="styles"
    @mousedown="interacted = true"
    @keydown.enter="interacted = true"
    @focusin="interacted = true"
  >
    <div
      class="input-container accessibility-outline outline-inset"
      :aria-disabled="disabled"
      :aria-required="isRequired"
      :aria-readonly="isReadOnly"
      :aria-describedby="inputAriaDescribedby"
      :aria-invalid="isAriaInvalid"
      :aria-label="sanitizedLabel || 'Auswahl'"
      :aria-placeholder="placeholderText"
    >
      <div class="input-container-inner">
        <div
          v-if="iconLeft"
          class="icon-left"
        >
          <BaseIcon :icon="iconLeft" />
        </div>
        <div
          class="input-field"
          @click="!range ? $refs.datepicker.focus() : !focused ? $refs.rangepickerStart.focus() : ''"
          @keydown.enter="!range ? $refs.datepicker.focus() : !focused ? $refs.rangepickerStart.focus() : ''"
        >
          <span
            v-if="placeholder && !hasValue"
            class="placeholder"
          >{{ placeholder }}</span>
          <label :for="name">{{ label }}</label>
          <input
            v-if="!range"
            :id="name"
            ref="datepicker"
            :pattern="isTime ? '[0-9]{2}:[0-9]{2}' : null"
            :type="isTime ? 'time' : 'text'"
            :name="name"
            :value="isTime ? time : date"
            :min="getMin"
            :max="getMax"
            :step="step || getDisplayFormat.includes('s') ? 1 : null"
            :required="isRequired"
            :readonly="readonly"
            :disabled="disabled"
            :autocomplete="autocomplete"
            :aria-disabled="disabled"
            :aria-required="isRequired"
            :aria-readonly="isReadOnly"
            :aria-invalid="isAriaInvalid"
            :aria-describedby="externalErrorId || undefined"
            :aria-label="sanitizedLabel || 'Datum'"
            :aria-placeholder="placeholderText"
            @keydown.enter.stop.prevent="range ? $refs.rangepickerStart.focus() : $refs.datepicker.focus()"
            @keydown.space.stop.prevent="range ? $refs.rangepickerStart.focus() : $refs.datepicker.focus()"
            @input="onInput($event.target.value)"
            @keydown="onKeyDown($event, { val: $event.target.value })"
            @focusin="focused = true"
            @focusout="onFocusOut($event)"
            @changeDate="changeDate($event)"
            @changeYear="changeDate($event)"
            @show="showPicker()"
            @hide="onDatepickerHide($event)"
          >
          <span
            v-if="isTime"
            class="input-field--overlay"
          />
          <div
            v-if="range"
            ref="rangepicker"
            class="rangepicker"
          >
            <input
              ref="rangepickerStart"
              :name="`${name}-start`"
              :value="date[0]"
              :min="getMin"
              :max="getMax"
              :required="isRequired"
              :readonly="readonly"
              :disabled="disabled"
              :autocomplete="autocomplete"
              :aria-disabled="disabled"
              :aria-required="isRequired"
              :aria-readonly="isReadOnly"
              :aria-invalid="hasError"
              :aria-label="sanitizedLabel || 'Startdatum'"
              :aria-placeholder="placeholderText"
              @keydown.enter.stop.prevent="range ? $refs.rangepickerStart.focus() : $refs.datepicker.focus()"
              @keydown.space.stop.prevent="range ? $refs.rangepickerStart.focus() : $refs.datepicker.focus()"
              @input="start = $event.target.value"
              @keydown="onKeyDown($event, { val: $event.target.value, isStart: true })"
              @focusin="focused = true"
              @focusout="onFocusOut($event)"
              @changeDate="changeDate($event, true)"
              @show="showPicker()"
            >
            <span>-</span>
            <input
              ref="rangepickerEnd"
              :style="{ 'pointer-events': !rangeEndDisabled ? 'auto' : 'none' }"
              :name="`${name}-end`"
              :value="date[1]"
              :min="getMin"
              :max="getMax"
              :required="isRequired"
              :readonly="readonly"
              :disabled="disabled"
              :autocomplete="autocomplete"
              :aria-disabled="disabled"
              :aria-required="isRequired"
              :aria-readonly="isReadOnly"
              :aria-invalid="hasError"
              :aria-label="sanitizedLabel || 'Enddatum'"
              :aria-placeholder="placeholderText"
              @input="end = $event.target.value"
              @keydown="onKeyDown($event, { val: $event.target.value, isStart: false })"
              @focusin="focused = true"
              @focusout="onFocusOut($event)"
              @changeDate="changeDate($event, false)"
              @show="showPicker()"
            >
          </div>
        </div>
        <div
          v-if="getIconRight || defaultIcon || (hasValue && clearable && !isTime && !disabled)"
          class="icon-right"
          :class="{ 'accessibility-outline': iconRightInteractive }"
          :role="iconRightInteractive ? 'button' : undefined"
          :tabindex="iconRightInteractive && !disabled ? 0 : -1"
          :aria-label="iconRightLabel || undefined"
          :aria-disabled="disabled"
          @click="hasValue && clearable && !isTime && !disabled ? clear($event, { val: range ? [] : '' }) : ''"
          @keydown.enter.prevent="hasValue && clearable && !isTime && !disabled ? clear($event, { val: range ? [] : '' }) : ''"
          @keydown.space.prevent="hasValue && clearable && !isTime && !disabled ? clear($event, { val: range ? [] : '' }) : ''"
        >
          <BaseIcon
            v-if="hasValue && clearable && !isTime && !disabled"
            icon="fa-light fa-xmark"
          />
          <BaseIcon
            v-else-if="getIconRight || defaultIcon"
            :icon="getIconRight || defaultIcon"
          />
        </div>
      </div>
    </div>
    <div
      v-if="hasError || (hint && (getMin || getMax)) || hintFormat"
      class="input-hint"
    >
      <div
        v-if="hasError"
        :id="`${name}-error`"
        class="input-hint--error"
        role="alert"
        aria-live="assertive"
        v-html="errorMessage"
      />
      <div
        v-if="hint && (getMin || getMax)"
        class="input-hint--counter"
      >
        <span>{{ createDate(getMin) }} {{ getMax ? ` - ${createDate(getMax)}` : '' }}</span>
      </div>
      <div
        v-else-if="hintFormat"
        class="input-hint--hint"
      >
        <span>Format: {{ lang === 'de' ? 'dd.mm.yyyy' : 'yyyy-mm-dd' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/extensions, import/no-unresolved
import de from 'vanillajs-datepicker/locales/de';
import { Datepicker, DateRangePicker } from 'vanillajs-datepicker';
import _ from 'lodash';
import { storeToRefs } from 'pinia';
import { position } from '@/helper/HtmlCss';

import BaseInputMixin from '../../../shared/mixins/BaseInputMixin';
import BaseDateTimePickerMixin from '../../../shared/mixins/BaseDateTimePickerMixin';
import ClickOutside from '../../../shared/mixins/ClickOutside';

import { useCenterPwaStore } from '@/apps/center/stores/pwa';

const VALID_TYPES = ['time', 'day', 'month', 'year'];
const VALID_LOCALES = ['en', 'de'];
const earliestDate = () => {
  const currentDate = new Date();
  const pastDate = new Date();

  pastDate.setFullYear(currentDate.getFullYear() - 150);

  const cutoffDate = new Date('1900-01-01');

  if (pastDate >= cutoffDate) {
    return pastDate;
  }
  return cutoffDate;
};

const DAY_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const utcMidnight = (year, month, day) => {
  const date = new Date(0);

  date.setUTCFullYear(year, month, day);

  return date;
};

const utcDay = (day) => {
  const parts = DAY_PATTERN.exec(day);

  if (parts) {
    return utcMidnight(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }

  // Timestamps and other date strings keep their local calendar day.
  const parsed = new Date(day);

  return Number.isNaN(parsed.getTime())
    ? null
    : utcMidnight(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

// Counts calendar days in UTC, so a clock change can neither add nor drop a day.
const daysBetween = (first, last) => {
  const days = [];
  const end = utcDay(last);
  const cursor = utcDay(first);

  while (cursor && end && cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return days;
};

export default {
  name: 'BaseDateTimePicker',
  mixins: [
    BaseInputMixin,
    BaseDateTimePickerMixin,
    ClickOutside,
  ],
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: [String, Number, Date, Array],
      required: false,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: () => 'day',
      validator: (value) => VALID_TYPES.includes(value),
    },
    range: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: Boolean,
      required: false,
      default: false,
    },
    hintFormat: {
      type: Boolean,
      required: false,
      default: false,
    },
    format: {
      type: String,
      required: false,
      default: '',
    },
    displayFormat: {
      type: String,
      required: false,
      default: '',
    },
    lang: {
      type: String,
      required: false,
      default: 'de',
      validator: (value) => VALID_LOCALES.includes(value),
    },
    datesDisabled: {
      type: Array,
      required: false,
      default: () => [],
    },
    datesAllowed: {
      type: Array,
      required: false,
      default: () => [],
    },
    daysOfWeekDisabled: {
      type: Array,
      required: false,
      default: () => [],
    },
    autoPrefill: {
      type: Boolean,
      required: false,
      default: false,
    },
    min: {
      type: [String, Date, Number],
      required: false,
      default: () => null,
    },
    max: {
      type: [String, Date, Number],
      required: false,
      default: () => null,
    },
    orientation: {
      type: String,
      required: false,
      default: 'auto',
    },
    step: {
      type: [Number, String],
      required: false,
      default: () => null,
    },
    container: {
      type: [String, HTMLElement],
      required: false,
      default: 'body',
    },
    emitOnMount: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxRange: {
      type: Number,
      required: false,
      default: null,
      validator: (value) => value === null || value > 0,
    },
    defaultViewDate: {
      type: [String, Date],
      required: false,
      default: () => new Date(),
    },
  },

  setup() {
    const pwaStore = useCenterPwaStore();
    const { isMobile } = storeToRefs(pwaStore);

    return {
      isMobile,
    };
  },
  data() {
    let date = null;

    if (this.range) {
      date = this.getFormat() === 'Date'
        ? _.compact([
          this.value?.[0] ? Datepicker.formatDate(this.value[0], 'yyyy-mm-dd') : '',
          this.value?.[1] ? Datepicker.formatDate(this.value[1], 'yyyy-mm-dd') : '',
        ])
        : _.compact([
          this.normalizeByValue(this.value?.[0] || ''),
          this.normalizeByValue(this.value?.[1] || ''),
        ]);
    } else {
      date = this.getFormat() === 'Date' && this.value
        ? Datepicker.formatDate(this.value, 'yyyy-mm-dd')
        : this.normalizeByValue(this.value || '');
    }

    let allowedDates = [];

    /**
     * Allow Deprecated Prop "available"
     */
    allowedDates = this.$attrs.available?.length
      ? this.$attrs.available.map((x) => this.normalizeByValue(x))
      : [];

    allowedDates = this.datesAllowed?.length
      ? this.datesAllowed.map((x) => this.normalizeByValue(x))
      : allowedDates;

    allowedDates = _.orderBy(allowedDates, (x) => x, 'asc');

    let disabledDates = this.datesDisabled?.length
      ? this.datesDisabled.map((x) => this.normalizeByValue(x))
      : [];

    disabledDates = _.orderBy(disabledDates, (x) => x, 'asc');

    const defaultIcon = () => {
      if (this.iconRight === '') {
        return (this.$attrs.mode || this.type) === 'time' ? 'fa-light fa-clock' : 'fa-light fa-calendar-days';
      }
      return undefined;
    };

    return {
      defaultIcon: defaultIcon(),
      datepickerRef: null,
      allowedDates,
      disabledDates,
      date,
      time: this.value ? this.normalizeByValue(this.value || '') : (this.min ? this.normalizeByValue(this.min) : null) || '00:00',
      start: this.value instanceof Array ? this.value[0] : '',
      end: this.value instanceof Array ? this.value[1] : '',
      timer: null,
      internalMin: this.min === null && this.type !== 'time' ? earliestDate() : this.min,
      internalMax: this.max,
      rangeEndDisabled: true,
      interacted: this.emitOnMount,
      activeInputDate: null,
      activeDatepickerDate: null,
      activeRangeIsStart: true,
      hasUserSelectedDate: false,
    };
  },
  computed: {
    gridTemplateColumns() {
      return this.iconLeft ? 'auto 1fr auto' : '1fr auto';
    },
    placeholderText() {
      return !this.hasValue ? this.placeholder : '';
    },
    hasValue() {
      const value = this.isTime ? this.time : this.date;
      const ifNumberValidCheck = _.isNumber(value) ? !Number.isNaN(value) : true;
      const ifArrayValidCheck = value instanceof Array ? value.length : true;

      return value !== '' && value !== null && value !== 'undefined' && ifNumberValidCheck && ifArrayValidCheck;
    },
    isTime() {
      return this.getType === 'time';
    },
    getDisplayFormat() {
      if (this.isValid(this.displayFormat)) {
        return this.displayFormat;
      }
      switch (this.getType) {
        case 'time':
          return 'h:m';
        case 'day':
          return 'dd. M yyyy';
        case 'month':
          return 'M yyyy';
        case 'year':
          return 'yyyy';
        default:
          return 'dd. M yyyy';
      }
    },
    hasRange() {
      return !!this.getMin && !!this.getMax;
    },
    getAllowedDates() {
      let range = [];
      if (this.hasRange) {
        range = daysBetween(this.getMin, this.getMax);
      } else {
        return [];
      }

      if (!this.allowedDates.length && !this.disabledDates.length) {
        return range;
      } if (!this.allowedDates.length) {
        return range.map((x) => (!this.disabledDates.includes(x) ? x : null)).filter(Boolean);
      } if (!this.disabledDates.length) {
        return range.map((x) => (this.allowedDates.includes(x) ? x : null)).filter(Boolean);
      }
      return range.map((x) => (this.allowedDates.includes(x) ? x : null)).map((x) => (!this.disabledDates.includes(x) ? x : null)).filter(Boolean);
    },
    getType() {
      return this.$attrs.mode || this.type;
    },
    getMin: {
      get() {
        return this.internalMin ? this.normalizeByValue(this.internalMin) : null;
      },
      set(val) {
        this.internalMin = val;
      },
    },
    getMax: {
      get() {
        return this.internalMax ? this.normalizeByValue(this.internalMax) : null;
      },
      set(val) {
        this.internalMax = val;
      },
    },
    iconRightInteractive() {
      return (!this.disabled && (
        (this.hasValue && this.clearable && !this.isTime)
      ));
    },
    iconRightLabel() {
      if (!this.iconRightInteractive) {
        return null;
      }
      if (this.hasValue && this.clearable && !this.isTime) {
        return 'Eingabe löschen';
      }

      return null;
    },
  },
  watch: {
    value(val, prev) {
      this.rangeEndDisabled = !(val instanceof Array && val.length > 0);
      if (
        this.range
        && prev
        && prev.every((x) => !x)
        && this.datepickerRef?.datepickers
        && val?.some((x) => x)
      ) {
        this.datepickerRef.datepickers.forEach((x, i) => {
          if (val[i]) {
            x.setDate(new Date(this.normalizeByValue(val[i])));
          }
        });
        this.date = [
          val[0] ? this.createDate(this.normalizeByValue(val[0])) : ' ',
          val[1] ? this.createDate(this.normalizeByValue(val[1])) : ' ',
        ];
      }
      if (!this.range && !prev) {
        this.date = this.createDate(val);
      }
    },
  },
  mounted() {
    if (this.allowedDates.length) {
      this.getMin = new Date(this.allowedDates[0]);
      this.getMax = new Date(this.allowedDates[this.allowedDates.length - 1]).addDays(1);
    }

    Object.assign(Datepicker.locales, de);
    const that = this;

    const config = {
      autohide: true,
      buttonClass: `${this.classDefault}--button`,
      container: this.container,
      datesDisabled: this.disabledDates,
      daysOfWeekDisabled: this.daysOfWeekDisabled || [],
      defaultViewDate: this.defaultViewDate,
      beforeShowDay: (date) => {
        // eslint-disable-next-line no-param-reassign
        date = Datepicker.formatDate(date, 'yyyy-mm-dd');
        return this.dateIsAllowed(date);
      },
      format: {
        toValue(date) {
          if (date instanceof Date) {
            return date;
          }

          if (!that.dateIsValid(date)) {
            // If the current date is invalid or empty, return a date based on defaultViewDate
            // instead of falling back to potentially invalid that.date
            if (!that.date || that.date === ' ' || that.date === '') {
              return new Date(that.defaultViewDate);
            }
            return new Date(that.date);
          }

          const normalized = that.normalizeByValue(date);
          const clamped = that.clampDateRange(normalized);
          return new Date(clamped);
        },
        toDisplay(date) {
          // Only process if user has interacted OR if we have a valid existing value
          // Don't set dates just from opening the picker or calendar navigation without selection
          const hasExistingValue = that.value && that.value !== '' && that.value !== ' ';
          const shouldProcessDate = that.hasUserSelectedDate || that.autoPrefill || hasExistingValue;

          if (!shouldProcessDate) {
            return '';
          }

          // eslint-disable-next-line no-param-reassign
          date = Datepicker.formatDate(date, 'yyyy-mm-dd');

          if (that.range) {
            // Determine slot by comparing the formatted date to each internal
            // datepicker's current date. The library updates datepicker[i].dates
            // before calling toDisplay, so this is reliable across all flows
            // (initial mount, single-side change, setDates refresh, both
            // pickers refresh in one tick). Falling back to focus / heuristics
            // here led to the slot being mis-assigned when both pickers held
            // values and focus had moved off the clicked input — the new value
            // overwrote the wrong side (IOSUITE-6840 follow-up).
            const rangepicker = that.datepickerRef
              || that.$refs.rangepicker?.rangepicker;
            const datepickers = rangepicker && rangepicker.datepickers;
            let isStart = null;
            if (datepickers) {
              const startDate = datepickers[0] && datepickers[0].getDate
                ? datepickers[0].getDate()
                : null;
              const endDate = datepickers[1] && datepickers[1].getDate
                ? datepickers[1].getDate()
                : null;
              const startISO = startDate
                ? Datepicker.formatDate(new Date(startDate), 'yyyy-mm-dd')
                : null;
              const endISO = endDate
                ? Datepicker.formatDate(new Date(endDate), 'yyyy-mm-dd')
                : null;
              if (startISO && date === startISO && date !== endISO) {
                isStart = true;
              } else if (endISO && date === endISO && date !== startISO) {
                isStart = false;
              }
            }
            if (isStart === null) {
              isStart = that.getActiveRangeIsStart();
            }
            const currentStart = that.date[0] ?? null;
            const currentEnd = that.date[1] ?? null;
            const displayDate = that.createDate(date);

            that.date = isStart
              ? [displayDate, currentEnd]
              : [currentStart, displayDate];

            const buildSafeDate = (raw, asDateFormat) => {
              if (!raw || raw === ' ') {
                return asDateFormat ? null : '';
              }
              const dateObj = new Date(that.normalizeByValue(raw));
              if (Number.isNaN(dateObj.getTime())) {
                return asDateFormat ? null : '';
              }
              if (!that.isTime) {
                dateObj.setTime(dateObj.startOfDay().getTime());
              }
              return dateObj;
            };

            const asDateFormat = that.getFormat() === 'Date';
            const start = buildSafeDate(that.date[0], asDateFormat);
            const end = buildSafeDate(that.date[1], asDateFormat);

            const returnFormat = asDateFormat
              ? [start, end]
              : [
                start ? Datepicker.formatDate(start, that.getFormat()) : '',
                end ? Datepicker.formatDate(end, that.getFormat()) : '',
              ];

            if (that.interacted) {
              that.emitUpdate(returnFormat);
            }
          } else {
            that.date = that.createDate(date);

            const dateObj = new Date(date);

            // Set time to 00:00:00 for date selections (not time)
            if (!that.isTime) {
              dateObj.setTime(dateObj.startOfDay().getTime());
            }

            const returnFormat = that.getFormat() === 'Date'
              ? dateObj
              : Datepicker.formatDate(dateObj, that.getFormat());

            if (that.interacted) {
              that.emitUpdate(returnFormat);
            }
          }

          return that.createDate(that.clampDateRange(Datepicker.formatDate(new Date(date), 'yyyy-mm-dd')));
        },
      },
      language: 'de',
      maxDate: this.max,
      minDate: this.min,
      allowOneSidedRange: true,
      nextArrow: '<i class="fa-thin fa-chevron-right"></i>',
      orientation: this.orientation,
      pickLevel: this.getPickLevel(),
      prevArrow: '<i class="fa-thin fa-chevron-left"></i>',
      weekStart: 1,
    };

    if (!this.isTime) {
      if (this.range) {
        this.datepickerRef = new DateRangePicker(this.$refs.rangepicker, config);
      } else {
        this.datepickerRef = new Datepicker(this.$refs.datepicker, config);

        const hasExistingValue = this.value !== null && this.value !== undefined && this.value !== '';
        if (hasExistingValue && !this.dateIsAllowed(Datepicker.formatDate(new Date(this.value), 'yyyy-mm-dd'))) {
          let newDate = this.defaultViewDate;

          if (this.allowedDates.length) {
            newDate = this.dateIsAllowed(this.defaultViewDate)
              ? this.defaultViewDate
              : this.firstAllowedDate();
          }

          this.datepickerRef.setDate(new Date(newDate));
        }
      }

      this.clickOutside('add', [this.$refs.datepickerRoot, this.datepickerRef.pickerElement], this.closeDatepicker);

      // Initialize tracked values after datepicker is ready
      this.$nextTick(() => {
        this.updateTrackedValues();
      });
    } else {
      // Needed until old implementation no longer uses the deprecated prop "mode"
      this.defaultIcon = 'fa-thin fa-clock';
    }
  },
  beforeDestroy() {
    // `datepickerRef` is only created in mounted() when `!isTime`, so it stays null for
    // time-only pickers — guard it the same way `showPicker` already does further down.
    this.clickOutside('remove', [this.$refs.datepickerRoot, this.datepickerRef?.pickerElement], this.closeDatepicker);
  },
  methods: {
    showPicker() {
      // Reset interaction state on open so mere opening doesn't commit a date
      this.interacted = false;
      this.hasUserSelectedDate = false;
      // Mobile position fix - will position the datepicker in the middle of the input field
      if (this.isMobile) {
        const { offsetWidth } = this.$refs.datepickerRoot;
        const { x: screenPosX } = position(this.$refs.datepickerRoot)[0];
        const datepickerRefs = this.range
          ? [...(this.datepickerRef.datepickers.map((el) => el.picker.element) || [])]
          : [this.datepickerRef.picker.element];
        const datepickerWidth = this.range
          ? datepickerRefs.map((el) => el.offsetWidth)
          : [datepickerRefs[0].offsetWidth];

        for (let i = 0; i < datepickerRefs.length; i += 1) {
          const datepickerPosition = screenPosX + ((offsetWidth - datepickerWidth[i]) / 2);

          datepickerRefs[i].style.left = `${datepickerPosition}px`;
        }
      }
    },
    closeDatepicker() {
      if (!this.range) {
        this.datepickerRef.hide();
      }
    },
    getFormat() {
      if (this.format) {
        return this.format;
      }
      if (this.getType === 'year') {
        return 'yyyy';
      }

      return 'yyyy-mm-dd';
    },
    createDate(val) {
      if (!val) {
        return this.isTime ? val : ' ';
      }

      if (this.isTime) {
        const times = [0, 0, 0].map((x, i) => parseInt(val.match(/\d+/g)[i], 10) || x);
        const dateTime = new Date().setHours(...times);

        const config = {};

        if (this.getDisplayFormat.includes('h')) {
          config.hour = '2-digit';
        }
        if (this.getDisplayFormat.includes('m')) {
          config.minute = '2-digit';
        }
        if (this.getDisplayFormat.includes('s')) {
          config.second = '2-digit';
        }

        return new Date(dateTime).toLocaleTimeString('de', config);
      }
      const date = this.normalizeByValue(val);

      return Datepicker.formatDate(new Date(date), this.getDisplayFormat, this.lang);
    },
    getPickLevel() {
      switch (this.getType) {
        case 'month':
          return 1;
        case 'year':
          return 2;

        default:
          return 0;
      }
    },
    normalizeByValue(val) {
      if (val instanceof Array && val.length === 0) {
        return [];
      }
      if (val instanceof Array) {
        return val.map((x) => this.normalizeByValue(x));
      }
      if (val instanceof Date) {
        return Datepicker.formatDate(val, 'yyyy-mm-dd');
      }
      // Add null/undefined check
      if (!val || typeof val !== 'string') {
        return val;
      }
      // Handle ISO datetime strings (e.g., "2023-05-11T00:00:00.000000Z")
      // Extract only the date portion before the 'T'
      let dateVal = val;
      if (val.includes('T')) {
        dateVal = val.split('T')[0];
      }
      if (dateVal.match(/\s/g)) {
        // Use a fallback format during initialization to avoid computed property issues
        const format = this.getDisplayFormat || 'dd. M yyyy';
        return this.normalizeDate(dateVal, format);
      } if (dateVal.includes('.')) {
        return this.normalizeDate(dateVal, 'dd.mm.yyyy');
      } if (dateVal.includes('-')) {
        return this.normalizeDate(dateVal, 'yyyy-mm-dd');
      }
      return dateVal;
    },
    clampDateRange(dateString) {
      const min = this.getMin ? new Date(this.getMin).getTime() : 0;
      const max = this.getMax ? new Date(this.getMax).getTime() : new Date(this.getMax).addYears(100);
      const date = new Date(dateString).getTime();

      if (!this.getMin && !this.getMax) {
        return dateString;
      } if (this.getMin && !this.getMax) {
        const result = min <= date ? date : min;
        return Datepicker.formatDate(result, 'yyyy-mm-dd');
      }

      let result = min;
      if (min <= date) {
        if (max >= date) {
          result = date;
        } else {
          result = max;
        }
      }

      return Datepicker.formatDate(result, 'yyyy-mm-dd');
    },
    clampTimeRange(timeString) {
      const min = this.getMin ? parseInt(this.getMin.replace(':', ''), 10) : 0;
      const max = this.getMax ? parseInt(this.getMax.replace(':', ''), 10) : 2359;
      const time = parseInt(timeString.replace(':', ''), 10);

      if (!this.getMin && !this.getMax) {
        return timeString;
      } if (this.getMin && !this.getMax) {
        const result = min <= time ? timeString : this.getMin;
        return result;
      }

      let result = this.getMin;
      if (min <= time) {
        if (max >= time) {
          result = timeString;
        } else {
          result = this.getMax;
        }
      }

      return result;
    },
    onInput(val) {
      if (this.isTime) {
        this.time = val;
      } else {
        this.date = val;
      }

      // Update tracked input value when user types
      this.$nextTick(() => {
        if (this.range) {
          // For range, we need to know which input was changed
          // This will be updated properly in the EnterHook
          const startInput = this.$refs.rangepickerStart;
          const endInput = this.$refs.rangepickerEnd;
          if (document.activeElement === startInput) {
            this.activeRangeIsStart = true;
            this.activeInputDate = startInput?.value || null;
          } else if (document.activeElement === endInput) {
            this.activeRangeIsStart = false;
            this.activeInputDate = endInput?.value || null;
          }
        } else if (this.$refs.datepicker) {
          this.activeInputDate = this.$refs.datepicker.value || null;
        }
      });
    },
    changeDate(e, isStart = false) {
      if (this.timer || e.type !== 'changeDate') {
        return;
      }

      let date = this.normalizeByValue(Datepicker.formatDate(e.detail.date, 'yyyy-mm-dd'));

      // Only mark as user-selected if we have a valid, non-1900 date OR if user has existing value
      // The datepicker library triggers changeDate with empty/invalid dates when opening with empty value
      const normalizedDate = `${date ?? ''}`.trim();
      const isEmptyDate = normalizedDate === '';
      const is1900Date = normalizedDate === '1900-01-01';
      const hasExistingValue = `${this.value ?? ''}`.trim() !== '';
      const isValidDateString = !(isEmptyDate || is1900Date);

      if (isValidDateString || hasExistingValue) {
        // Mark as interacted only when an actual date is being applied (not empty or default 1900 date)
        this.interacted = true;
        this.hasUserSelectedDate = true;
      }

      this.timer = setTimeout(() => {
        clearTimeout(this.timer);
        this.timer = null;
      }, 100);

      if (!this.dateIsValid(date)) {
        date = this.normalizeByValue(Datepicker.formatDate(new Date(this.value), 'yyyy-mm-dd'));
      }

      if (!this.dateIsAllowed(date)) {
        date = this.firstAllowedDate();
      }
      const DATE = new Date(date).getTime();
      const START = new Date(this.date[0]).getTime();
      const END = new Date(this.date[1]).getTime();

      if (this.range) {
        // maxRange validation
        if (this.maxRange !== null) {
          const daysDiff = Math.abs(Math.floor((DATE - (isStart ? END : START)) / (1000 * 60 * 60 * 24)));
          if (daysDiff > this.maxRange) {
            const referenceDate = new Date(isStart ? END : START);
            date = Datepicker.formatDate(
              new Date(referenceDate).addDays(isStart ? -this.maxRange : this.maxRange),
              'yyyy-mm-dd',
            );
          }
        }

        if (isStart && DATE >= END) {
          const startDate = date;
          const endDate = Datepicker.formatDate(new Date(date).addDays(), 'yyyy-mm-dd');

          this.date = [startDate, endDate];

          this.$nextTick(() => this.datepickerRef.setDates(new Date(startDate), new Date(endDate)));
        } else if (!isStart && DATE <= START) {
          const startDate = Datepicker.formatDate(new Date(date).subDays(), 'yyyy-mm-dd');
          const endDate = date;

          this.date = [startDate, endDate];

          this.$nextTick(() => this.datepickerRef.setDates(new Date(startDate), new Date(endDate)));
        } else {
          if (isStart) {
            this.date = [date, this.date[1] ?? null];
          } else {
            this.date = [this.date[0] ?? null, date];
          }
          // Only refresh the picker that actually changed. Previously we
          // called `setDates(start, end)` and re-passed `this.date[1]` (a
          // display-formatted string like "15. Jun 2026") which `toValue`
          // could not parse back into a Date. The end picker also re-ran
          // `format.toDisplay`, and slot detection at that moment could pick
          // the wrong side and overwrite the just-set start with the end's
          // display value (IOSUITE-6840 follow-up).
          this.$nextTick(() => {
            const targetIndex = isStart ? 0 : 1;
            const target = this.datepickerRef
              && this.datepickerRef.datepickers
              && this.datepickerRef.datepickers[targetIndex];
            if (target && typeof target.setDate === 'function') {
              target.setDate(new Date(date));
            }
          });
        }
        // Update tracked values after range datepicker changes
        this.$nextTick(() => {
          this.updateTrackedValues(isStart);
        });

        if (isStart) {
          setTimeout(() => {
            this.$refs.rangepickerEnd.focus();
          }, 200);
        } else {
          setTimeout(() => {
            this.unfocus();
          }, 200);
        }
      } else {
        // Only update the date if user has actually selected something
        if (this.hasUserSelectedDate) {
          this.date = date;
        }

        this.$nextTick(() => {
          if (this.hasUserSelectedDate) {
            this.datepickerRef.setDate(new Date(date));
          }
          // Update tracked values after datepicker changes via keyboard navigation
          this.updateTrackedValues(isStart);
        });
      }
    },
    FocusOutHook() {
      if (this.isTime) {
        const clampedTime = this.createDate(this.clampTimeRange(this.time));
        this.time = clampedTime;
        this.emitUpdate(clampedTime);
        this.unfocus();
      }
    },
    ClearHook({ val }) {
      this.date = val;
    },
    EnterHook({ val, isStart }) {
      // Treat pressing Enter / confirming as a real interaction
      this.interacted = true;
      this.hasUserSelectedDate = true;
      // Get current datepicker internal date
      let currentDatepickerDate = null;
      let focusedDate = null;
      let isPickerOpen = false;

      if (this.datepickerRef) {
        if (this.range && this.datepickerRef.datepickers) {
          const pickerIndex = isStart ? 0 : 1;
          const picker = this.datepickerRef.datepickers[pickerIndex];
          currentDatepickerDate = picker?.getDate?.();
          isPickerOpen = picker?.picker?.active || false;

          // Try to get the focused date from the picker
          if (isPickerOpen && picker?.picker) {
            // The viewDate property contains the currently focused/highlighted date
            if (picker.picker.viewDate) {
              focusedDate = new Date(picker.picker.viewDate);
            }
          }
        } else if (!this.range) {
          currentDatepickerDate = this.datepickerRef.getDate?.();
          isPickerOpen = this.datepickerRef.picker?.active || false;

          // Try to get the focused date from the picker
          if (isPickerOpen && this.datepickerRef.picker) {
            // The viewDate property contains the currently focused/highlighted date
            if (this.datepickerRef.picker.viewDate) {
              focusedDate = new Date(this.datepickerRef.picker.viewDate);
            }
          }
        }
      }

      // If the datepicker is open, ALWAYS use its focused date (keyboard navigation case)
      // Otherwise, try to parse the input value (manual typing case)
      let dateToUse = null;

      if (isPickerOpen) {
        // Picker is open - use focused date if available, otherwise current date
        if (focusedDate) {
          dateToUse = focusedDate;
        } else if (currentDatepickerDate) {
          dateToUse = currentDatepickerDate;
        }
      } else if (val && val.trim() !== '') {
        // Picker is closed - user typed manually, try to parse the input
        try {
          const normalizedVal = this.normalizeByValue(val);
          if (normalizedVal && this.dateIsValid(normalizedVal)) {
            dateToUse = new Date(normalizedVal);
          } else if (currentDatepickerDate) {
            // Parsing failed, fall back to datepicker value if available
            dateToUse = currentDatepickerDate;
          }
        } catch (e) {
          // If parsing fails, fall back to datepicker value if available
          if (currentDatepickerDate) {
            dateToUse = currentDatepickerDate;
          }
        }
      } else if (currentDatepickerDate) {
        // No input value but we have a datepicker date, use it
        dateToUse = currentDatepickerDate;
      }

      // Apply the date if we determined one
      if (dateToUse) {
        this.changeDate({ detail: { date: dateToUse }, type: 'changeDate' }, isStart);
        // Update tracked values after successful change
        this.$nextTick(() => {
          this.updateTrackedValues(isStart);
        });
      } else {
        // Final fallback: try to parse input value
        this.changeDate({ detail: { date: new Date(this.normalizeByValue(val)) }, type: 'changeDate' }, isStart);
        this.$nextTick(() => {
          this.updateTrackedValues(isStart);
        });
      }
    },
    unfocus() {
      document.activeElement.blur();
    },
    dateIsAllowed(date) {
      let result = true;
      if (this.hasRange) {
        result = this.getAllowedDates.includes(date);
      } else if (this.disabledDates.length && this.allowedDates.length) {
        result = !this.disabledDates.includes(date) && this.allowedDates.includes(date);
      } else if (this.disabledDates.length) {
        result = !this.disabledDates.includes(date);
      } else if (this.allowedDates.length) {
        result = this.allowedDates.includes(date);
      }
      return result;
    },
    firstAllowedDate() {
      let result = null;
      if (this.hasRange) {
        [result] = this.getAllowedDates;
      } else if (this.disabledDates.length && this.allowedDates.length) {
        [result] = this.allowedDates.map((x) => (!this.disabledDates.includes(x) ? x : null)).filter(Boolean);
      } else if (this.disabledDates.length) {
        result = Datepicker.formatDate(new Date(), 'yyyy-mm-dd');
      } else if (this.allowedDates.length) {
        [result] = this.allowedDates;
      } else {
        result = Datepicker.formatDate(new Date(), 'yyyy-mm-dd');
      }
      return result;
    },
    dateIsValid(date) {
      // eslint-disable-next-line no-param-reassign
      date = this.normalizeByValue(date);
      return date.match(/[a-z|A-Z]/g) === null;
    },
    ClassesHook() {
      return {
        [`${this.classDefault}--${this.getType}`]: this.getType,
        [`${this.classDefault}--range`]: this.range,
        [`${this.classDefault}--step-${this.step}`]: this.step !== null,
      };
    },
    onDatepickerHide() {
      // When datepicker hides, check if a date was selected via keyboard navigation
      // If so, we want to use that date instead of the input value
      if (this.datepickerRef && !this.range) {
        // Do not commit any date if the user hasn't interacted and there is no existing value
        // This prevents setting a default date (e.g., 01.01.1900) when clicking outside with empty input
        if (!this.interacted && !this.hasValue) {
          return;
        }
        const selectedDate = this.datepickerRef.getDate();
        if (selectedDate) {
          this.changeDate({ detail: { date: selectedDate }, type: 'changeDate' });
        }
      }
    },
    getActiveRangeIsStart() {
      const startInput = this.$refs.rangepickerStart;
      const endInput = this.$refs.rangepickerEnd;

      if (document.activeElement === endInput) {
        return false;
      }
      if (document.activeElement === startInput) {
        return true;
      }

      const hasDisplayValue = (val) => val && val !== ' ' && `${val}`.trim() !== '';
      const hasStart = hasDisplayValue(this.date[0]);
      const hasEnd = hasDisplayValue(this.date[1]);

      if (!hasStart) {
        return true;
      }
      if (!hasEnd) {
        return false;
      }

      return this.activeRangeIsStart !== false;
    },
    updateTrackedValues(isStart = false) {
      // Update tracked input date
      if (this.range) {
        this.activeRangeIsStart = isStart;
        const inputRef = isStart ? this.$refs.rangepickerStart : this.$refs.rangepickerEnd;
        this.activeInputDate = inputRef?.value || null;
      } else if (this.$refs.datepicker) {
        this.activeInputDate = this.$refs.datepicker.value || null;
      }

      // Update tracked datepicker internal date
      if (this.datepickerRef) {
        if (this.range && this.datepickerRef.datepickers) {
          const pickerIndex = isStart ? 0 : 1;
          const datepicker = this.datepickerRef.datepickers[pickerIndex];
          this.activeDatepickerDate = datepicker?.getDate?.() || null;
        } else if (!this.range && this.datepickerRef.getDate) {
          this.activeDatepickerDate = this.datepickerRef.getDate() || null;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.placeholder {
  position: absolute;
  background-color: transparent;
  cursor: text;
}

.input-container-inner {
  display: grid;
  grid-template-columns: v-bind(gridTemplateColumns);
}
</style>

<style lang="scss">
  .datepicker.datepicker-dropdown {
    z-index: 10000 !important;
  }
</style>
